// İSG uzmanı portalı — firma oluştur/düzenle formu (Modal + RHF + Zod).
//
// api-contract §9.4: create `{ name, legalName?, taxNumber?, admin?:{ email,
// password, firstName, lastName } }`. `admin` verilirse firmaya bağlı bir
// CorporateAdmin (tenant-scoped; sistem-admin DEĞİL) kurulur. Parola politikası
// (§2: min 8 + harf + rakam) FE'de de doğrulanır; backend `400 PASSWORD_POLICY_ERROR`.
// E-posta kullanımda → `409 USER_ALREADY_EXISTS`. Update: `{ name, legalName?, taxNumber? }`.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  type CompanyAdminDto,
  Field,
  Input,
  Modal,
  Spinner,
  adminApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { adminKeys } from '../../lib/adminKeys';

interface CompanyFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma (+opsiyonel ilk yönetici). */
  company?: CompanyAdminDto | null;
  onSuccess?: () => void;
}

export function CompanyFormModal({
  open,
  onOpenChange,
  company,
  onSuccess,
}: CompanyFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(company);

  const schema = useMemo(
    () =>
      z
        .object({
          name: z.string().trim().min(1, t('admin.validation.nameRequired')),
          legalName: z.string().trim().optional(),
          taxNumber: z.string().trim().optional(),
          withAdmin: z.boolean(),
          adminFirstName: z.string().trim().optional(),
          adminLastName: z.string().trim().optional(),
          adminEmail: z.string().trim().optional(),
          adminPassword: z.string().optional(),
        })
        .superRefine((val, ctx) => {
          if (!val.withAdmin) {
            return;
          }
          if (!val.adminFirstName) {
            ctx.addIssue({
              path: ['adminFirstName'],
              code: z.ZodIssueCode.custom,
              message: t('admin.validation.firstNameRequired'),
            });
          }
          if (!val.adminLastName) {
            ctx.addIssue({
              path: ['adminLastName'],
              code: z.ZodIssueCode.custom,
              message: t('admin.validation.lastNameRequired'),
            });
          }
          if (!val.adminEmail) {
            ctx.addIssue({
              path: ['adminEmail'],
              code: z.ZodIssueCode.custom,
              message: t('admin.validation.emailRequired'),
            });
          } else if (!z.string().email().safeParse(val.adminEmail).success) {
            ctx.addIssue({
              path: ['adminEmail'],
              code: z.ZodIssueCode.custom,
              message: t('admin.validation.emailInvalid'),
            });
          }
          const pw = val.adminPassword ?? '';
          if (pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw)) {
            ctx.addIssue({
              path: ['adminPassword'],
              code: z.ZodIssueCode.custom,
              message: t('admin.validation.passwordPolicy'),
            });
          }
        }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, watch, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: company?.name ?? '',
      legalName: company?.legalName ?? '',
      taxNumber: company?.taxNumber ?? '',
      withAdmin: false,
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      adminPassword: '',
    },
  });

  const withAdmin = watch('withAdmin');

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const legalName = values.legalName && values.legalName.length > 0 ? values.legalName : null;
      const taxNumber = values.taxNumber && values.taxNumber.length > 0 ? values.taxNumber : null;
      if (company) {
        return adminApi.updateCompany(company.uuid, { name: values.name, legalName, taxNumber });
      }
      return adminApi.createCompany({
        name: values.name,
        legalName,
        taxNumber,
        admin: values.withAdmin
          ? {
              firstName: values.adminFirstName ?? '',
              lastName: values.adminLastName ?? '',
              email: values.adminEmail ?? '',
              password: values.adminPassword ?? '',
            }
          : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.companies() });
      if (company) {
        queryClient.invalidateQueries({ queryKey: adminKeys.company(company.uuid) });
      }
      reset();
      onSuccess?.();
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('admin.companies.edit.title') : t('admin.companies.create.title')}
      closeLabel={t('admin.common.close')}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('admin.common.cancel')}
          </Button>
          <Button type="submit" form="company-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('admin.common.save')} />
            ) : isEdit ? (
              t('admin.common.save')
            ) : (
              t('admin.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="company-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="company-name"
          label={t('admin.companies.field.name')}
          error={formState.errors.name?.message}
          required
        >
          <Input id="company-name" invalid={Boolean(formState.errors.name)} {...register('name')} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="company-legal" label={t('admin.companies.field.legalName')}>
            <Input id="company-legal" {...register('legalName')} />
          </Field>
          <Field htmlFor="company-tax" label={t('admin.companies.field.taxNumber')}>
            <Input id="company-tax" {...register('taxNumber')} />
          </Field>
        </div>

        {!isEdit ? (
          <div className="flex flex-col gap-4 rounded-md border border-border bg-surface-muted/40 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-fg">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--color-primary)]"
                {...register('withAdmin')}
              />
              {t('admin.companies.adminToggle')}
            </label>
            <p className="text-xs text-muted">{t('admin.companies.adminHint')}</p>

            {withAdmin ? (
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    htmlFor="admin-first"
                    label={t('admin.companies.field.adminFirstName')}
                    error={formState.errors.adminFirstName?.message}
                    required
                  >
                    <Input
                      id="admin-first"
                      invalid={Boolean(formState.errors.adminFirstName)}
                      autoComplete="off"
                      {...register('adminFirstName')}
                    />
                  </Field>
                  <Field
                    htmlFor="admin-last"
                    label={t('admin.companies.field.adminLastName')}
                    error={formState.errors.adminLastName?.message}
                    required
                  >
                    <Input
                      id="admin-last"
                      invalid={Boolean(formState.errors.adminLastName)}
                      autoComplete="off"
                      {...register('adminLastName')}
                    />
                  </Field>
                </div>
                <Field
                  htmlFor="admin-email"
                  label={t('admin.companies.field.adminEmail')}
                  error={formState.errors.adminEmail?.message}
                  required
                >
                  <Input
                    id="admin-email"
                    type="email"
                    autoComplete="off"
                    invalid={Boolean(formState.errors.adminEmail)}
                    {...register('adminEmail')}
                  />
                </Field>
                <Field
                  htmlFor="admin-password"
                  label={t('admin.companies.field.adminPassword')}
                  hint={t('admin.validation.passwordPolicy')}
                  error={formState.errors.adminPassword?.message}
                  required
                >
                  <Input
                    id="admin-password"
                    type="password"
                    autoComplete="new-password"
                    invalid={Boolean(formState.errors.adminPassword)}
                    {...register('adminPassword')}
                  />
                </Field>
              </div>
            ) : null}
          </div>
        ) : null}
      </form>
    </Modal>
  );
}
